import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate, useParams } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { CartProvider } from './context/CartContext';
import { Toaster } from './components/ui/sonner';
import { AnnouncementBar } from './components/custom/AnnouncementBar';
import { Header } from './components/custom/Header';
import { ScrollToTop } from './components/custom/ScrollToTop';
import { SocialProofNotification } from './components/custom/SocialProofNotification';
import { ExitIntentPopup } from './components/custom/ExitIntentPopup';
import { FirstTimeDiscountBanner } from './components/custom/FirstTimeDiscountBanner';
import { StickyCartBar } from './components/custom/StickyCartBar';
import { trackPageView, startHeartbeat, stopHeartbeat } from './utils/analytics';
import HomePage from './pages/HomePage';
import ProductListingPage from './pages/ProductListingPage';
import ProductDetailPage from './pages/ProductDetailPage';
import CartPage from './pages/CartPage';
import CheckoutPage from './pages/CheckoutPage';
import OrdersPage from './pages/OrdersPage';
import AdminPage from './pages/AdminPage';
import AdminDashboardPage from './pages/admin/AdminDashboardPage';
import AdminProductsPage from './pages/admin/AdminProductsPage';
import AdminOrdersPage from './pages/admin/AdminOrdersPage';
import AdminMarketingPage from './pages/admin/AdminMarketingPage';
import AdminVideosPage from './pages/admin/AdminVideosPage';
import AdminSettingsPage from './pages/admin/AdminSettingsPage';
import AdminCouponsPage from './pages/admin/AdminCouponsPage';
import AdminReviewsPage from './pages/admin/AdminReviewsPage';
import AdminAnalyticsPage from './pages/admin/AdminAnalyticsPage';
import AdminLogisticsPage from './pages/admin/AdminLogisticsPage';
import AdminInsightsPage from './pages/admin/AdminInsightsPage';
import OrderSuccessPage from './pages/OrderSuccessPage';
import ShippingPolicyPage from './pages/ShippingPolicyPage';
import ReturnsPolicyPage from './pages/ReturnsPolicyPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import TermsPage from './pages/TermsPage';
import './App.css';

// Redirect component for /product/:id to /products/:id
function ProductRedirect() {
  const { id } = useParams();
  const location = useLocation();
  // Preserve query params (utm_source, fbclid, etc.)
  return <Navigate to={`/products/${id}${location.search}`} replace />;
}

// Page view tracker component
function PageTracker() {
  const location = useLocation();
  
  useEffect(() => {
    const path = location.pathname;
    let pageName = 'Home';
    
    if (path.includes('/products/')) pageName = 'Product Detail';
    else if (path === '/products') pageName = 'Product Listing';
    else if (path === '/cart') pageName = 'Cart';
    else if (path === '/checkout') pageName = 'Checkout';
    else if (path === '/order-success') pageName = 'Order Success';
    else if (path === '/orders') pageName = 'My Orders';
    else if (path.includes('/admin')) pageName = 'Admin';
    
    // Don't track admin pages
    if (!path.includes('/admin')) {
      trackPageView(pageName, path);
    }
  }, [location]);
  
  // Start heartbeat for live user tracking
  useEffect(() => {
    const path = location.pathname;
    // Don't track admin pages
    if (!path.includes('/admin')) {
      startHeartbeat();
    }
    
    return () => {
      stopHeartbeat();
    };
  }, [location.pathname]);
  
  return null;
}

// Track Facebook Pixel events (Pixel is initialized in index.html)
export const trackPixelEvent = (eventName, data = {}) => {
  if (window.fbq) {
    window.fbq('track', eventName, data);
  }
};

function App() {
  const [products, setProducts] = useState([]);

  // Fetch products for social proof notifications
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/products?limit=10`);
        const data = await response.json();
        setProducts(data.products || []);
      } catch (error) {
        console.log('Could not fetch products for social proof');
      }
    };
    fetchProducts();
  }, []);

  return (
    <HelmetProvider>
      <CartProvider>
        <Router>
          <ScrollToTop />
          <PageTracker />
          <SocialProofNotification products={products} />
          <ExitIntentPopup />
          <div className="App">
            <FirstTimeDiscountBanner />
            <AnnouncementBar />
            <Header />
            <StickyCartBar />
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/products" element={<ProductListingPage />} />
              <Route path="/products/:id" element={<ProductDetailPage />} />
              <Route path="/product/:id" element={<ProductRedirect />} />
            <Route path="/cart" element={<CartPage />} />
            <Route path="/checkout" element={<CheckoutPage />} />
            <Route path="/order-success" element={<OrderSuccessPage />} />
            <Route path="/orders" element={<OrdersPage />} />
            <Route path="/admin" element={<AdminPage />} />
            <Route path="/admin/dashboard" element={<AdminDashboardPage />} />
            <Route path="/admin/products" element={<AdminProductsPage />} />
            <Route path="/admin/orders" element={<AdminOrdersPage />} />
            <Route path="/admin/videos" element={<AdminVideosPage />} />
            <Route path="/admin/marketing" element={<AdminMarketingPage />} />
            <Route path="/admin/settings" element={<AdminSettingsPage />} />
            <Route path="/admin/coupons" element={<AdminCouponsPage />} />
            <Route path="/admin/reviews" element={<AdminReviewsPage />} />
            <Route path="/admin/analytics" element={<AdminAnalyticsPage />} />
            <Route path="/admin/logistics" element={<AdminLogisticsPage />} />
            <Route path="/admin/insights" element={<AdminInsightsPage />} />
            <Route path="/shipping-policy" element={<ShippingPolicyPage />} />
            <Route path="/returns-policy" element={<ReturnsPolicyPage />} />
            <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
            <Route path="/terms" element={<TermsPage />} />
          </Routes>
          <Toaster position="bottom-center" richColors offset="20px" />
          
          {/* Footer */}
          <footer className="bg-muted mt-20 py-12 px-4 border-t">
            <div className="container mx-auto max-w-6xl">
              <div className="grid md:grid-cols-4 gap-8">
                <div>
                  <h3 className="font-semibold mb-4">Tiffin & Bag</h3>
                  <p className="text-sm text-muted-foreground">
                    Fun lunchboxes and cool school bags for kids. Free delivery on all orders!
                  </p>
                  <p className="text-xs text-muted-foreground mt-3">
                    A brand by F Square Media
                  </p>
                </div>
                <div>
                  <h3 className="font-semibold mb-4">Shop</h3>
                  <ul className="space-y-2 text-sm">
                    <li><a href="/products?category=lunchbox" className="text-muted-foreground hover:text-foreground">Lunchboxes</a></li>
                    <li><a href="/products?category=bag" className="text-muted-foreground hover:text-foreground">School Bags</a></li>
                  </ul>
                </div>
                <div>
                  <h3 className="font-semibold mb-4">Support</h3>
                  <ul className="space-y-2 text-sm">
                    <li><a href="/shipping-policy" className="text-muted-foreground hover:text-foreground">Shipping Policy</a></li>
                    <li><a href="/returns-policy" className="text-muted-foreground hover:text-foreground">Returns & Refunds</a></li>
                    <li><a href="mailto:motheringin@gmail.com" className="text-muted-foreground hover:text-foreground">Contact Us</a></li>
                  </ul>
                </div>
                <div>
                  <h3 className="font-semibold mb-4">Legal</h3>
                  <ul className="space-y-2 text-sm">
                    <li><a href="/privacy-policy" className="text-muted-foreground hover:text-foreground">Privacy Policy</a></li>
                    <li><a href="/terms" className="text-muted-foreground hover:text-foreground">Terms & Conditions</a></li>
                  </ul>
                </div>
              </div>
              <div className="mt-8 pt-8 border-t text-center text-sm text-muted-foreground">
                <p>&copy; 2026 F Square Media. All rights reserved.</p>
                <p className="text-xs mt-1">Panchsheel Park, New Delhi - 110017 | +91 80621 80632</p>
              </div>
            </div>
          </footer>
        </div>
      </Router>
    </CartProvider>
    </HelmetProvider>
  );
}

export default App;